import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';

import { saveFiltersInSessionStorage, getFiltersFromSessionStorage } from './utils/filtersInSessionStorage';

type StateProps = Record<string, string>;

interface FilterProviderProps {
  children: ReactNode;
}

const FilterStateContext = createContext<
  | {
      filterTags: StateProps;
      setFilterIsManuallyChanged: Dispatch<SetStateAction<boolean>>;
      setFilterTags: Dispatch<SetStateAction<StateProps>>;
    }
  | undefined
>(undefined);

const FilterProvider = ({ children }: FilterProviderProps) => {
  const [filterIsManuallyChanged, setFilterIsManuallyChanged] = useState(false);
  const [filterTags, setFilterTags] = useState<StateProps>({});

  useEffect(() => {
    setFilterIsManuallyChanged(false);
    setFilterTags(getFiltersFromSessionStorage());
  }, []);

  if (filterIsManuallyChanged) saveFiltersInSessionStorage(filterTags);

  const contextValue = useMemo(
    () => ({ filterTags, setFilterIsManuallyChanged, setFilterTags }),
    [filterTags, setFilterIsManuallyChanged, setFilterTags],
  );

  return <FilterStateContext.Provider value={contextValue}>{children}</FilterStateContext.Provider>;
};

const useFilter = () => {
  const context = useContext(FilterStateContext);
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};

export { FilterProvider, useFilter };
